import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import { COST_METHOD, COST_SCOPE, GOLD_RATE_SOURCE } from '../../utils/finance/financeCostConstants';
import { formatTry } from '../../utils/finance/financeFormat';
import {
  COST_KIND,
  applyHamRuleOverride,
  listHamCatalogSpecs,
  previewHamUnitCost,
  RESAT_PREMIUM,
  TAKI_DEFAULT_MILYEM
} from '../../utils/finance/financeHamVeri';
import { TAKI_URUN_TIPLERI } from '../../utils/takiUrunTipleri';
import { getLiveHasRates } from '../../utils/finance/financeGoldRate';
import { fx, fxCardSx, fxPrimaryBtnSx, fxSectionTitleSx } from './financeTheme';

export const COST_TABS = [
  { id: 'Sarrafiye', label: 'Sarrafiye' },
  { id: 'Gram Altın', label: 'Gram Altın' },
  { id: 'Takı Ürün', label: 'Takı Ürün' },
  { id: 'Hurda', label: 'Hurda' },
  { id: 'Döviz', label: 'Döviz' }
];

const SARRAFIYE_GROUPS = [
  { id: 'eski', title: 'Eski Ziynet' },
  { id: 'yeni', title: 'Yeni Ziynet' },
  { id: 'ata', title: 'Cumhuriyet Ata' },
  { id: 'resat', title: 'Reşat' }
];

const GRAM_GROUPS = [
  { id: 'gram24', title: '24 Ayar' },
  { id: 'gram22', title: '22 Ayar' }
];

function ruleForProduct(rules, urun) {
  return (rules || []).find((r) => r.active !== false && r.scopeType === COST_SCOPE.PRODUCT_TYPE && r.scopeId === urun) || null;
}

function CostTable({ columns, rows, onEdit, canEdit }) {
  return (
    <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
      <Box component="thead">
        <Box component="tr">
          {columns.map((col) => (
            <Box
              component="th"
              key={col.key}
              sx={{
                textAlign: col.align || 'right',
                fontSize: 11.5,
                fontWeight: 700,
                color: fx.muted,
                p: '4px 6px',
                borderBottom: `1px solid ${fx.border}`
              }}
            >
              {col.label}
            </Box>
          ))}
          {canEdit ? (
            <Box component="th" sx={{ width: 72, p: '4px 6px', borderBottom: `1px solid ${fx.border}` }} />
          ) : null}
        </Box>
      </Box>
      <Box component="tbody">
        {rows.map((row) => (
          <Box component="tr" key={row.key}>
            {columns.map((col) => (
              <Box
                component="td"
                key={col.key}
                sx={{
                  p: '3px 6px',
                  fontSize: col.key === 'urun' ? 12.5 : 12,
                  fontWeight: col.bold || col.key === 'urun' ? 800 : 600,
                  color: col.key === 'urun' ? fx.text : fx.muted,
                  textAlign: col.align || 'right',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {col.render ? col.render(row) : row[col.key]}
              </Box>
            ))}
            {canEdit ? (
              <Box component="td" sx={{ p: '2px 4px', textAlign: 'right' }}>
                <Button size="small" onClick={() => onEdit(row)} sx={{ fontWeight: 700, color: fx.text, textTransform: 'none', minWidth: 0, px: 0.75, fontSize: 12 }}>
                  Düzenle
                </Button>
              </Box>
            ) : null}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function GroupCard({ title, children }) {
  return (
    <Box sx={{ ...fxCardSx, p: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {title ? <Typography sx={{ ...fxSectionTitleSx, mb: 0.6 }}>{title}</Typography> : null}
      {children}
    </Box>
  );
}

export default function FinanceCostCatalog({
  prices,
  rules = [],
  settings,
  canEdit,
  onSaveRule
}) {
  const [tab, setTab] = useState('Sarrafiye');
  const [edit, setEdit] = useState({ open: false, row: null, draft: {} });
  const liveHas = getLiveHasRates(prices);

  const hamByCategory = useMemo(() => ({
    Sarrafiye: listHamCatalogSpecs('Sarrafiye'),
    'Gram Altın': listHamCatalogSpecs('Gram Altın'),
    Hurda: listHamCatalogSpecs('Hurda'),
    Döviz: listHamCatalogSpecs('Döviz')
  }), []);

  const openEdit = (row) => {
    setEdit({
      open: true,
      row,
      draft: {
        hamMultiplier: row.spec?.factor ?? '',
        extraFactor: row.spec?.extraFactor ?? '',
        milyemOverride: row.spec?.milyem ?? row.milyem ?? '',
        goldRateSource: row.rule?.goldRateSource || settings?.defaultGoldRateSource || GOLD_RATE_SOURCE.LIVE_BUY
      }
    });
  };

  const saveEdit = async () => {
    const row = edit.row;
    if (!row) return;
    if (row.kind === 'taki') {
      await onSaveRule({
        scopeType: COST_SCOPE.PRODUCT_TYPE,
        scopeId: row.urun,
        name: row.urun,
        category: 'Takı Ürün',
        costMethod: COST_METHOD.GOLD_FORMULA,
        goldRateSource: edit.draft.goldRateSource,
        milyemOverride: edit.draft.milyemOverride,
        active: true
      }, row.rule?.id);
    } else {
      await onSaveRule({
        scopeType: COST_SCOPE.PRODUCT_TYPE,
        scopeId: row.urun,
        name: row.urun,
        category: row.spec?.kategori,
        costMethod: COST_METHOD.HAM_VERI_SELL,
        hamCode: row.spec?.hamCode,
        hamMultiplier: edit.draft.hamMultiplier,
        extraFactor: edit.draft.extraFactor,
        milyemOverride: edit.draft.milyemOverride,
        active: true
      }, row.rule?.id);
    }
    setEdit({ open: false, row: null, draft: {} });
  };

  const hamRows = (list) => list.map((spec) => {
    const rule = ruleForProduct(rules, spec.urun);
    const merged = applyHamRuleOverride(spec, rule) || spec;
    const unit = previewHamUnitCost(merged, prices, { gram: 1, side: 'sale' });
    const buyUnit = previewHamUnitCost(merged, prices, { gram: 1, side: 'buy' });
    return { key: spec.urun, urun: spec.urun, spec: merged, rule, kind: spec.kind, unit, buyUnit };
  });

  const takiRows = TAKI_URUN_TIPLERI.map((urun) => {
    const rule = ruleForProduct(rules, urun);
    const milyem = Number(rule?.milyemOverride) > 0 ? Number(rule.milyemOverride) : TAKI_DEFAULT_MILYEM[urun];
    const unit = liveHas.buy > 0 && milyem > 0 ? Math.round(liveHas.buy * milyem * 100) / 100 : null;
    return { key: urun, urun, kind: 'taki', milyem, rule, unit, formula: `gram × ${milyem} × has alış` };
  });

  let body = null;
  if (tab === 'Sarrafiye') {
    body = (
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1, minHeight: 0, height: '100%' }}>
        {SARRAFIYE_GROUPS.map((group) => (
          <GroupCard key={group.id} title={group.title}>
            <CostTable
              canEdit={canEdit}
              onEdit={openEdit}
              columns={[
                { key: 'urun', label: 'Ürün', align: 'left' },
                { key: 'formula', label: 'Formül', align: 'left', render: (row) => row.spec.formula },
                { key: 'unit', label: 'Anlık', bold: true, render: (row) => (row.unit == null ? '—' : formatTry(row.unit)) }
              ]}
              rows={hamRows(hamByCategory.Sarrafiye.filter((s) => s.family === group.id))}
            />
          </GroupCard>
        ))}
      </Box>
    );
  } else if (tab === 'Gram Altın') {
    body = (
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1, minHeight: 0, height: '100%' }}>
        {GRAM_GROUPS.map((group) => (
          <GroupCard key={group.id} title={group.title}>
            <CostTable
              canEdit={canEdit}
              onEdit={openEdit}
              columns={[
                { key: 'urun', label: 'Ürün', align: 'left' },
                { key: 'formula', label: 'Formül', align: 'left', render: (row) => row.spec.formula },
                { key: 'unit', label: 'Anlık', bold: true, render: (row) => (row.unit == null ? '—' : formatTry(row.unit)) }
              ]}
              rows={hamRows(hamByCategory['Gram Altın'].filter((s) => s.family === group.id))}
            />
          </GroupCard>
        ))}
      </Box>
    );
  } else if (tab === 'Takı Ürün') {
    body = (
      <GroupCard title="Takı Ürün">
        <CostTable
          canEdit={canEdit}
          onEdit={openEdit}
          columns={[
            { key: 'urun', label: 'Ürün tipi', align: 'left' },
            { key: 'milyem', label: 'Milyem', render: (row) => row.milyem },
            { key: 'formula', label: 'Formül', align: 'left' },
            { key: 'unit', label: '1 g anlık', bold: true, render: (row) => (row.unit == null ? '—' : formatTry(row.unit)) }
          ]}
          rows={takiRows}
        />
      </GroupCard>
    );
  } else if (tab === 'Hurda') {
    body = (
      <GroupCard title="Hurda">
        <CostTable
          canEdit={canEdit}
          onEdit={openEdit}
          columns={[
            { key: 'urun', label: 'Ürün', align: 'left' },
            { key: 'milyem', label: 'Milyem', render: (row) => row.spec.milyem },
            { key: 'formula', label: 'Formül', align: 'left', render: (row) => row.spec.formula },
            { key: 'unit', label: '1 g bozdurma', bold: true, render: (row) => (row.unit == null ? '—' : formatTry(row.unit)) }
          ]}
          rows={hamRows(hamByCategory.Hurda)}
        />
      </GroupCard>
    );
  } else {
    const fxRows = hamRows(hamByCategory.Döviz);
    const mid = Math.ceil(fxRows.length / 2);
    body = (
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1, minHeight: 0, height: '100%' }}>
        <GroupCard title="Döviz">
          <CostTable
            canEdit={canEdit}
            onEdit={openEdit}
            columns={[
              { key: 'urun', label: 'Ürün', align: 'left' },
              { key: 'buyUnit', label: 'Alış', render: (row) => (row.buyUnit == null ? '—' : formatTry(row.buyUnit)) },
              { key: 'unit', label: 'Satış', bold: true, render: (row) => (row.unit == null ? '—' : formatTry(row.unit)) }
            ]}
            rows={fxRows.slice(0, mid)}
          />
        </GroupCard>
        <GroupCard title=" ">
          <CostTable
            canEdit={canEdit}
            onEdit={openEdit}
            columns={[
              { key: 'urun', label: 'Ürün', align: 'left' },
              { key: 'buyUnit', label: 'Alış', render: (row) => (row.buyUnit == null ? '—' : formatTry(row.buyUnit)) },
              { key: 'unit', label: 'Satış', bold: true, render: (row) => (row.unit == null ? '—' : formatTry(row.unit)) }
            ]}
            rows={fxRows.slice(mid)}
          />
        </GroupCard>
      </Box>
    );
  }

  const editingTaki = edit.row?.kind === 'taki';
  const editingMelt = edit.row?.spec?.kind === COST_KIND.MELT;
  const editingResat = edit.row?.spec?.family === 'resat';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }} data-testid="finance-cost-catalog">
      <Tabs
        value={tab}
        onChange={(_, next) => setTab(next)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          minHeight: 38,
          mb: 1.1,
          flexShrink: 0,
          borderBottom: `1px solid ${fx.border}`,
          '& .MuiTabs-indicator': { height: 2.5, borderRadius: 2, bgcolor: '#1d4ed8' },
          '& .MuiTab-root': {
            minHeight: 38,
            py: 0,
            px: 1.5,
            fontWeight: 750,
            fontSize: 13,
            minWidth: 0,
            color: fx.muted,
            textTransform: 'none'
          },
          '& .Mui-selected': { color: '#1d4ed8 !important' }
        }}
      >
        {COST_TABS.map((item) => <Tab key={item.id} value={item.id} label={item.label} />)}
      </Tabs>
      <Box sx={{ minHeight: 0, overflow: 'hidden', flex: 1, display: 'flex', flexDirection: 'column' }}>{body}</Box>

      <Dialog
        open={Boolean(edit.open && edit.row)}
        onClose={() => setEdit({ open: false, row: null, draft: {} })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: 16 }}>{edit.row?.urun || 'Düzenle'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, pt: '12px !important' }} onClick={(e) => e.stopPropagation()}>
          {editingTaki || editingMelt ? (
            <TextField
              size="small"
              label="Milyem"
              value={edit.draft.milyemOverride}
              onChange={(e) => setEdit((p) => ({ ...p, draft: { ...p.draft, milyemOverride: e.target.value } }))}
            />
          ) : (
            <TextField
              size="small"
              label="Çarpan"
              value={edit.draft.hamMultiplier}
              onChange={(e) => setEdit((p) => ({ ...p, draft: { ...p.draft, hamMultiplier: e.target.value } }))}
              helperText={edit.row?.spec?.formula}
            />
          )}
          {editingResat ? (
            <TextField
              size="small"
              label="Reşat prim"
              value={edit.draft.extraFactor}
              onChange={(e) => setEdit((p) => ({ ...p, draft: { ...p.draft, extraFactor: e.target.value } }))}
              helperText={`Varsayılan ${RESAT_PREMIUM}`}
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={(e) => { e.stopPropagation(); setEdit({ open: false, row: null, draft: {} }); }}>Vazgeç</Button>
          <Button variant="contained" sx={fxPrimaryBtnSx} onClick={saveEdit}>Kaydet</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
